import React from "react";
import { DraggableProvided } from "react-beautiful-dnd";

import { Chip, Stack, Typography } from "@mui/material";
import { DealCharacteristicsModal } from "components/Characteristics/Deal-characteristics-modal/DealCharacteristicsModal";
import { usePrompt } from "hooks";
import { DealStageItem } from "store/types";

import { getAssetClassName, getCardChips } from "../Pipeline/pipeline-utils";

interface Props extends DealStageItem {
  /** Absent while grouping is active — the card renders static (no drag). */
  draggableProvided?: DraggableProvided;
  isDragging?: boolean;
  stageId: number;
  /** Characteristic names to render as chips; falls back to the asset-class subtitle when empty. */
  cardCharacteristicKeys?: string[];
  /** Called after the Deal characteristics modal saves — the board refetches so chips reflect changes. */
  onSaved?: () => void;
}

/**
 * Deal card used while the grouping feature is active — matches the Figma design (node 2549:29170):
 * title + characteristic chips on a `#f6f8fc` surface, no note/avatars footer.
 */
export const PipelineDealCard: React.FC<Props> = (props) => {
  const { id, title, draggableProvided, isDragging = false, cardCharacteristicKeys = [], onSaved } = props;
  const asset_class = props.asset_class;
  const openPrompt = usePrompt();

  const chips = cardCharacteristicKeys.length ? getCardChips(props, cardCharacteristicKeys) : [];

  // Clicking a card opens the Deal characteristics modal (it does NOT navigate into the deal).
  const handleClick = async (e: React.MouseEvent) => {
    if (isDragging) return;
    const target = e.target as HTMLElement;
    if (target.closest("button")) return;
    const saved = await openPrompt({
      Component: DealCharacteristicsModal,
      componentType: "modal",
      props: { dealId: id, dealName: title }
    });
    if (saved) onSaved?.();
  };

  const dragProps = draggableProvided
    ? {
        ref: (node: HTMLElement | null) => {
          if (typeof draggableProvided.innerRef === "function") {
            draggableProvided.innerRef(node);
          } else if (draggableProvided.innerRef) {
            (draggableProvided.innerRef as React.MutableRefObject<HTMLElement | null>).current = node;
          }
        },
        ...draggableProvided.draggableProps,
        ...draggableProvided.dragHandleProps,
        style: {
          ...draggableProvided.draggableProps.style,
          userSelect: "none" as const,
          touchAction: "none" as const
        }
      }
    : {};

  return (
    <Stack
      {...dragProps}
      onClick={handleClick}
      gap="4px"
      sx={{
        bgcolor: "#f6f8fc",
        borderRadius: "8px",
        p: "8px",
        cursor: draggableProvided ? "grab" : "pointer",
        transition: "background-color 0.15s linear, box-shadow 0.15s linear",
        "&:hover": { bgcolor: "#eef2f9", boxShadow: "0px 1px 4px rgba(10, 49, 66, 0.12)" },
        "&:active": draggableProvided ? { cursor: "grabbing" } : undefined
      }}
    >
      <Typography sx={{ fontSize: "16px", lineHeight: "24px", color: "#071f35", wordBreak: "break-word" }}>
        {title}
      </Typography>

      {cardCharacteristicKeys.length ? (
        // Characteristics are configured — show their chips (or nothing). No asset-class fallback here,
        // otherwise a deal missing the chosen field would wrongly show its asset class.
        chips.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap="4px">
            {chips.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.value}
                sx={{
                  height: "24px",
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#071f35",
                  bgcolor: "#dbe8f5",
                  borderRadius: "20px",
                  "& .MuiChip-label": { px: "8px" }
                }}
              />
            ))}
          </Stack>
        )
      ) : (
        // Nothing configured — fall back to the asset-class subtitle.
        getAssetClassName(asset_class) && (
          <Typography sx={{ fontSize: "13px", lineHeight: "16px", color: "#92a7bc", wordBreak: "break-word" }}>
            {getAssetClassName(asset_class)}
          </Typography>
        )
      )}
    </Stack>
  );
};
